import {connect} from 'react-redux';

import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {isTeammateNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResolveStateProps} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {
  getCanAutocompleteMentionContacts,
  getCanInviteVisitors,
  getCompany,
} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {getConversations} from '../../../../store/data/dataSelectors';
import {getUserOwnedInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {makeGetDraftChannel} from '../../../../store/data/messages/messagesSelectors';
import {
  getMentionableAliases,
  getMentionableEmails,
  makeGetIsTeamMemberForNamespace,
  makeGetMentionableTeammatesForNamespace,
} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindOnlineStatus} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {ShareDropdown} from './shareDropdown';

/*
 * Props.
 */

interface ShareDropdownContainerProps {
  message: DraftMessageModel;
  onShareMessage: (trackers: ReadonlyArray<number | string>, commentText: string | undefined) => void;
}

/*
 * Selectors.
 */

function getMessage(_state: AppState, props: ShareDropdownContainerProps) {
  return props.message;
}

function makeGetConversation() {
  return createSelector(
    [getConversations, getMessage],
    (conversations, message) => conversations[message.conversationId],
  );
}

function makeGetNamespace() {
  const getChannel = makeGetDraftChannel(getMessage);
  return createSelector([getChannel], (channel) => channel.namespace);
}

function makeGetCanComment(
  getConversation: ReturnType<typeof makeGetConversation>,
  getNamespace: ReturnType<typeof makeGetNamespace>,
) {
  return createSelector(
    [getHasPermission, getConversation, getNamespace],
    (hasPermission, conversation, namespace) => {
      const permissionNamespace = conversation ? conversation.namespace : namespace;
      return hasPermission(permissionNamespace, PermissionsEnum.CONVERSATION_COMMENT);
    },
  );
}

function makeGetConversationInboxIds(getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector([getConversation, getUserOwnedInboxes], (conversation, userOwnedInboxes) => {
    if (!conversation) {
      return [];
    }

    if (isTeammateNamespace(conversation.namespace)) {
      return userOwnedInboxes.map((inbox) => inbox.id);
    }

    return conversation.inboxIds;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ShareDropdownContainerProps>()(() => {
  const getNamespace = makeGetNamespace();
  const getConversation = makeGetConversation();
  const getMentionableTeammates = makeGetMentionableTeammatesForNamespace(getNamespace);

  return {
    company: getCompany,
    teammates: getMentionableTeammates,
    mentionableAliases: getMentionableAliases,
    mentionableEmails: getMentionableEmails,
    teammateSuggestions: getMentionableTeammates,
    isTeamMember: makeGetIsTeamMemberForNamespace(getNamespace),
    canInviteVisitors: getCanInviteVisitors,
    canAutocompleteMentionContacts: getCanAutocompleteMentionContacts,
    canComment: makeGetCanComment(getConversation, getNamespace),
    findOnlineStatus: getFindOnlineStatus,
    user: getUser,
    conversationInboxIds: makeGetConversationInboxIds(getConversation),
    conversation: getConversation,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  dataUpdated: dataUpdatedAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ShareDropdownContainerProps,
) {
  const {onShareMessage, ...remainingProps} = ownProps;
  const addTrackers = (
    trackers: ReadonlyArray<number | string>,
    commentText: string | undefined,
    trackerTeammates: ReadonlyArray<TeammateModel>,
  ) => {
    // Write the new teammates into Redux since the conversation will now depend on them.
    dispatchProps.dataUpdated({teammates: trackerTeammates});
    onShareMessage(trackers, commentText);
  };

  return {
    ...stateProps,
    ...remainingProps,
    addTrackers,
  };
}

export const ShareDropdownContainer = connect(mapStateToProps, mapDispatchToProps, mergeProps)(ShareDropdown);
